(function () {
  const SELECTORS = {
    root: "[data-back-in-stock]",
    open: "[data-back-in-stock-open]",
    panel: "[data-back-in-stock-panel]",
    form: "[data-back-in-stock-form]",
    email: "[data-back-in-stock-email]",
    submit: "[data-back-in-stock-submit]",
    submitLabel: "[data-back-in-stock-submit-label]",
    status: "[data-back-in-stock-status]",
    variantSelect: "[data-variant-select]",
    variantJson: "[data-back-in-stock-variants]",
    copyJson: "[data-back-in-stock-copy]",
    optIn: "[data-back-in-stock-opt-in]",
  };
  const KLAVIYO_REVISION = "2024-10-15";
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function detectCompanyId() {
    const scripts = document.querySelectorAll("script[src*='klaviyo']");
    for (const script of scripts) {
      try {
        const url = new URL(script.src, window.location.origin);
        const companyId = url.searchParams.get("company_id");
        if (companyId) return companyId;
      } catch (error) {
        // Ignore malformed script URLs
      }

      const legacyMatch = script.src.match(/onsite\/js\/([A-Za-z0-9]+)\.js/i);
      if (legacyMatch && legacyMatch[1] && legacyMatch[1] !== "klaviyo") {
        return legacyMatch[1];
      }
    }

    return "";
  }

  function parseJson(root, selector, fallback) {
    const jsonEl = root.querySelector(selector);
    if (!jsonEl) return fallback;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch (error) {
      return fallback;
    }
  }

  function parseVariants(root) {
    return parseJson(root, SELECTORS.variantJson, {});
  }

  function parseCopy(root) {
    return parseJson(root, SELECTORS.copyJson, {});
  }

  function setStatus(els, type, message) {
    if (!els.status) return;

    els.status.hidden = !message;
    els.status.textContent = message || "";
    els.status.classList.toggle("form-status__message", Boolean(message));
    els.status.classList.toggle("form-status__message--success", type === "success");
    els.status.classList.toggle("form-status__message--error", type === "error");
  }

  function setSubmitting(els, submitting) {
    if (els.submit) {
      els.submit.disabled = submitting;
      els.submit.setAttribute("aria-busy", submitting ? "true" : "false");
    }

    if (els.submitLabel) {
      els.submitLabel.textContent = submitting ? els.copy.submitting : els.copy.button;
    }
  }

  function getSelectedVariantId(els) {
    if (els.variantSelect && els.variantSelect.value) {
      return els.variantSelect.value;
    }

    return els.root.dataset.variantId || "";
  }

  function isSoldOut(els, variantId) {
    if (!variantId) return false;
    return els.variants[variantId] === false;
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function setExpanded(els, expanded) {
    els.root.dataset.expanded = expanded ? "true" : "false";

    if (els.open) {
      els.open.hidden = expanded;
      els.open.setAttribute("aria-expanded", expanded ? "true" : "false");
    }

    if (els.panel) {
      els.panel.hidden = !expanded;
    }

    if (expanded && els.email) {
      els.email.focus();
    }
  }

  function updateVisibility(els) {
    const variantId = getSelectedVariantId(els);
    const soldOut = isSoldOut(els, variantId);
    const showNotify = isDesignMode() || soldOut;

    els.root.dataset.variantId = variantId;
    els.root.hidden = !showNotify;

    if (els.root.hidden) {
      setStatus(els, "", "");
      setExpanded(els, false);
    }

    // Blocks marked with data-hide-when-sold-out (quantity, buy buttons, gift note)
    // stay editable in the theme editor even when the notify form is force-shown.
    const section = els.root.closest(".shopify-section") || document;
    section.querySelectorAll("[data-hide-when-sold-out]").forEach((el) => {
      el.hidden = soldOut;
    });
  }

  // Create Client Subscription is what sets "Subscribes to Email Marketing".
  // The Back in Stock endpoint only queues restock alerts — it does not record
  // marketing consent. A list ID is required by this Klaviyo client endpoint.
  async function subscribeToEmailMarketing(companyId, listId, email) {
    const response = await fetch(
      "https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(companyId),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: "2025-10-15",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Back in stock form",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: "SUBSCRIBED",
                        },
                      },
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: "list",
                  id: listId,
                },
              },
            },
          },
        }),
      }
    );

    if (!(response.status === 202 || response.ok)) {
      throw new Error("newsletter");
    }
  }

  async function subscribe(els, email, variantId, optIn) {
    const companyId = els.root.dataset.companyId || detectCompanyId();
    if (!companyId) {
      throw new Error(els.copy.error);
    }

    const response = await fetch(
      "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" + encodeURIComponent(companyId),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              channels: ["EMAIL"],
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                  },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: "$shopify:::$default:::" + variantId,
                },
              },
            },
          },
        }),
      }
    );

    if (!(response.status === 202 || response.ok)) {
      throw new Error(els.copy.error);
    }

    if (!optIn) return;

    const listId = (els.root.dataset.newsletterListId || "").trim();
    if (!listId) {
      console.warn(
        "[back-in-stock] Newsletter checkbox was checked, but no Klaviyo list ID is set on the block. Email marketing consent was not recorded."
      );
      return;
    }

    try {
      await subscribeToEmailMarketing(companyId, listId, email);
    } catch (error) {
      console.warn("[back-in-stock] email marketing subscribe failed", error);
    }
  }

  function initRoot(root) {
    if (root.dataset.backInStockReady === "true") return;
    root.dataset.backInStockReady = "true";

    if (!root.dataset.companyId) {
      const detected = detectCompanyId();
      if (detected) root.dataset.companyId = detected;
    }

    const section = root.closest(".shopify-section") || document;
    const els = {
      root,
      open: root.querySelector(SELECTORS.open),
      panel: root.querySelector(SELECTORS.panel),
      form: root.querySelector(SELECTORS.form),
      email: root.querySelector(SELECTORS.email),
      submit: root.querySelector(SELECTORS.submit),
      submitLabel: root.querySelector(SELECTORS.submitLabel),
      status: root.querySelector(SELECTORS.status),
      optIn: root.querySelector(SELECTORS.optIn),
      variantSelect: section.querySelector(SELECTORS.variantSelect),
      variants: parseVariants(root),
      copy: parseCopy(root),
    };

    if (!els.form || !els.email || !els.open || !els.panel) return;

    setExpanded(els, false);
    updateVisibility(els);
    section.addEventListener("change", () => updateVisibility(els), true);

    els.open.addEventListener("click", () => {
      setExpanded(els, true);
    });

    els.form.addEventListener("submit", async (event) => {
      event.preventDefault();

      const email = els.email.value.trim();
      const variantId = getSelectedVariantId(els);

      if (!EMAIL_PATTERN.test(email)) {
        setStatus(els, "error", els.copy.invalidEmail);
        els.email.focus();
        return;
      }

      if (!variantId || !isSoldOut(els, variantId)) {
        setStatus(els, "error", els.copy.error);
        return;
      }

      setSubmitting(els, true);
      setStatus(els, "", "");

      try {
        const optIn = Boolean(els.optIn && els.optIn.checked);
        await subscribe(els, email, variantId, optIn);
        setStatus(els, "success", els.copy.success);
        els.form.reset();
      } catch (error) {
        setStatus(els, "error", els.copy.error);
      } finally {
        setSubmitting(els, false);
      }
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTORS.root).forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
